import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import type { GalleryAlbum } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { mediaUrl } from "@/lib/media";

export default function GallerySection() {
  const { data, loading, error } = useApi<GalleryAlbum[]>("gallery.php?size=4");
  const albums = (data ?? []).slice(0, 4);

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              활동갤러리
            </h2>
            <p className="mt-2 text-foreground-600">클럽의 생생한 활동 현장을 만나보세요.</p>
          </div>
          <Link
            to="/gallery"
            className="whitespace-nowrap inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
          >
            전체보기
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-32 w-full rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        ) : error ? (
          <EmptyState icon="ri-error-warning-line" title="갤러리를 불러오지 못했습니다" description={error} />
        ) : albums.length === 0 ? (
          <EmptyState icon="ri-image-line" title="등록된 활동 사진이 없습니다" />
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {albums.map((a) => (
              <Link
                key={a.no}
                to={`/gallery/${a.no}`}
                className="group overflow-hidden rounded-xl border border-background-200 bg-background-50"
              >
                <div className="relative h-32 md:h-40 w-full overflow-hidden">
                  {a.photo || a.thumb ? (
                    <img
                      src={mediaUrl(a.photo || a.thumb)}
                      alt={a.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-background-100 text-foreground-300">
                      <i className="ri-image-line text-3xl" aria-hidden="true"></i>
                    </div>
                  )}
                </div>
                <div className="p-3 md:p-4">
                  <div className="truncate text-sm font-medium text-foreground-900">{a.title}</div>
                  <div className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-camera-line" aria-hidden="true"></i>
                    {typeof a.cnt === "number" ? `${a.cnt}장` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}